import React, { useMemo, useState } from 'react';
import { LayoutGridIcon, ListIcon, WrenchIcon } from 'lucide-react';
import { Employee, JobStatus, ProJob } from '../../types/pro';
import { JOB_STATUS, formatDate, formatTime, hoursOnJob } from '../../utils/pro';
import { TimeEntry } from '../../types/pro';
import { PageHeader } from '../../components/pro/PageHeader';
import { SearchField } from '../../components/pro/SearchField';
import { StatusChip } from '../../components/pro/StatusChip';
import { EmptyState } from '../../components/pro/EmptyState';
interface ProJobsProps {
  jobs: ProJob[];
  employees: Employee[];
  timeEntries: TimeEntry[];
  onOpen: (jobId: string) => void;
  onSetStatus: (job: ProJob, status: JobStatus) => void;
}
const ORDER: JobStatus[] = ['ikke-planlagt', 'planlagt', 'i-gang', 'afventer', 'faerdig', 'faktureret'];
export function ProJobs({
  jobs,
  employees,
  timeEntries,
  onOpen,
  onSetStatus
}: ProJobsProps) {
  const [view, setView] = useState<'liste' | 'braet'>(() => {
    if (typeof window === 'undefined') return 'liste';
    return localStorage.getItem('proJobsView') as 'liste' | 'braet' || 'liste';
  });
  const [query, setQuery] = useState('');
  const [dragId, setDragId] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState<JobStatus | null>(null);
  const setViewMode = (next: 'liste' | 'braet') => {
    setView(next);
    localStorage.setItem('proJobsView', next);
  };
  const employeeById = useMemo(() => new Map(employees.map(e => [e.id ?? '', e])), [employees]);
  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return jobs;
    return jobs.filter(job => [job.title, job.jobNo, job.customerName, job.address, job.city].join(' ').toLowerCase().includes(term));
  }, [jobs, query]);
  const active = filtered.filter(job => !['faerdig', 'faktureret'].includes(job.status)).length;
  const renderMeta = (job: ProJob) => {
    const registered = hoursOnJob(timeEntries, job.id ?? '');
    const over = registered > job.estimatedHours && job.estimatedHours > 0;
    return <span className={`whitespace-nowrap text-xs tabular-nums ${over ? 'font-semibold text-amber-600' : 'text-stone-500'}`} data-id="element-1090" data-mp="3iw8jel8y7257">
        {registered.toLocaleString('da-DK')} / {job.estimatedHours} t
      </span>;
  };
  return <div data-id="element-1091" data-mp="1e5w9rmgyhg1">
      <PageHeader title="Opgaver" subtitle={`${active} aktive af ${jobs.length}`} actions={<div role="group" aria-label="Visning" className="flex rounded-lg border border-stone-200 bg-white p-1" data-id="element-1093" data-mp="6cpgnnlezl6a">
            {[{
        id: 'liste' as const,
        label: 'Liste',
        icon: ListIcon
      }, {
        id: 'braet' as const,
        label: 'Bræt',
        icon: LayoutGridIcon
      }].map(option => {
        const Icon = option.icon;
        return <button key={option.id} onClick={() => setViewMode(option.id)} aria-pressed={view === option.id} className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${view === option.id ? 'bg-stone-900 text-white' : 'text-stone-500 hover:text-stone-900'}`} data-id="element-1094" data-mp="p5tfroheiq8a">
                  <Icon className="h-3.5 w-3.5" data-id="element-1095" data-mp="1qxnk3aet8qk6" />
                  {option.label}
                </button>;
      })}
          </div>} data-id="element-1092" data-mp="1fqo39hefma7" />

      <SearchField value={query} onChange={setQuery} placeholder="Søg opgave, kunde eller adresse…" className="mb-4 max-w-md" data-id="element-1096" data-mp="1zx0v47il1n70" />

      {filtered.length === 0 ? <EmptyState icon={WrenchIcon} title="Ingen opgaver matcher søgningen" data-id="element-1097" data-mp="3ba3bb1grsaqn" /> : view === 'liste' ? <div className="overflow-hidden rounded-xl border border-stone-200 bg-white" data-id="element-1098" data-mp="36bs0ooinhw7d">
          <ul className="divide-y divide-stone-100" data-id="element-1099" data-mp="5idt3jzsojvo">
            {[...filtered].sort((a, b) => ORDER.indexOf(a.status) - ORDER.indexOf(b.status) || (a.start ?? '').localeCompare(b.start ?? '')).map(job => {
          const employee = employeeById.get(job.employeeIds[0] ?? '');
          return <li key={job.id} data-id="element-1100" data-mp="ofoga685jmur">
                    <button onClick={() => job.id && onOpen(job.id)} className="flex w-full flex-wrap items-center gap-x-4 gap-y-1.5 px-4 py-3.5 text-left transition-colors hover:bg-stone-50" data-id="element-1101" data-mp="tf1bbe96g08f">
                      <span className="w-24 flex-shrink-0 font-mono text-xs text-stone-400" data-id="element-1102" data-mp="134b3eu40tiwj">
                        {job.jobNo}
                      </span>
                      <span className="min-w-0 flex-1" data-id="element-1103" data-mp="1c50zlyx71bas">
                        <span className="block truncate text-sm font-medium text-stone-900" data-id="element-1104" data-mp="1c3g5gaesjudg">
                          {job.title}
                        </span>
                        <span className="block truncate text-xs text-stone-500" data-id="element-1105" data-mp="2skx1vcykybnu">
                          {job.customerName} · {job.address}, {job.city}
                        </span>
                      </span>
                      <span className="hidden w-28 flex-shrink-0 text-xs text-stone-500 sm:block" data-id="element-1106" data-mp="2ipd40l0gh2wg">
                        {job.start ? `${formatDate(job.start)} ${formatTime(job.start)}` : 'Ikke planlagt'}
                      </span>
                      {employee && <span className="hidden items-center gap-1.5 text-xs font-medium text-stone-600 md:flex" data-id="element-1107" data-mp="36jei6pm5olvq">
                          <span className="h-2 w-2 rounded-full" style={{
                  backgroundColor: employee.color
                }} data-id="element-1108" data-mp="2wnycrrwezz3" />
                          {employee.name.split(' ')[0]}
                        </span>}
                      {renderMeta(job)}
                      <StatusChip meta={JOB_STATUS[job.status]} data-id="element-1109" data-mp="1webu3lwtdupq" />
                    </button>
                  </li>;
        })}
          </ul>
        </div> : <div className="-mx-4 overflow-x-auto px-4 pb-2 sm:-mx-6 sm:px-6" data-id="element-1110" data-mp="1nm9shkb0e8dv">
          <div className="flex min-w-max gap-3" data-id="element-1111" data-mp="18q2besuwdn2t">
            {ORDER.map(status => {
          const items = filtered.filter(job => job.status === status);
          const isTarget = dragOver === status;
          return <section key={status} onDragOver={e => {
            e.preventDefault();
            setDragOver(status);
          }} onDragLeave={() => setDragOver(null)} onDrop={() => {
            const job = filtered.find(j => j.id === dragId);
            if (job && job.status !== status) onSetStatus(job, status);
            setDragId(null);
            setDragOver(null);
          }} className={`flex w-[260px] flex-shrink-0 flex-col rounded-xl border transition-colors ${isTarget ? 'border-brand-400 bg-brand-50/60' : 'border-stone-200 bg-stone-50'}`} data-id="element-1112" data-mp="zqfikvg8hpld">
                  <header className="flex items-center gap-2 border-b border-stone-200/70 px-3 py-2.5" data-id="element-1113" data-mp="3rutn3ujvw6kc">
                    <span className={`h-2 w-2 rounded-full ${JOB_STATUS[status].dot}`} data-id="element-1114" data-mp="3mzo1kwue8tws" />
                    <h2 className="flex-1 truncate text-sm font-semibold text-stone-800" data-id="element-1115" data-mp="qmtgqbzemy1">
                      {JOB_STATUS[status].label}
                    </h2>
                    <span className="rounded bg-white px-1.5 py-0.5 text-xs font-semibold tabular-nums text-stone-600" data-id="element-1116" data-mp="26jlgbf5cbzb5">
                      {items.length}
                    </span>
                  </header>

                  <ul className="flex-1 space-y-2 p-2" data-id="element-1117" data-mp="2r0k7x6mqzkkv">
                    {items.map(job => <li key={job.id} data-id="element-1118" data-mp="2vzeriasjhd61">
                        <div draggable onDragStart={() => setDragId(job.id ?? null)} onDragEnd={() => {
                  setDragId(null);
                  setDragOver(null);
                }} className={`rounded-lg border border-stone-200 bg-white p-3 transition-shadow hover:shadow-sm ${dragId === job.id ? 'opacity-50' : ''}`} data-id="element-1119" data-mp="30s7uou8d0dg0">
                          <button onClick={() => job.id && onOpen(job.id)} className="w-full text-left" data-id="element-1120" data-mp="2m1h3noeldidy">
                            <p className="font-mono text-[10px] text-stone-400" data-id="element-1121" data-mp="1bhz7owr9rg3">
                              {job.jobNo}
                            </p>
                            <p className="mt-0.5 truncate text-sm font-semibold text-stone-900" data-id="element-1122" data-mp="6hoka8sfa9yt">
                              {job.title}
                            </p>
                            <p className="mt-0.5 truncate text-xs text-stone-500" data-id="element-1123" data-mp="1p3tzh8q6f01">
                              {job.customerName}
                            </p>
                            <p className="mt-2 flex items-center justify-between gap-2 border-t border-stone-100 pt-2 text-xs text-stone-400" data-id="element-1124" data-mp="3oxqxs3oklawb">
                              <span data-id="element-1125" data-mp="3ey005t4pqfs0">
                                {job.start ? formatDate(job.start) : '—'}
                              </span>
                              {renderMeta(job)}
                            </p>
                          </button>

                          <label className="mt-2 block lg:hidden" data-id="element-1126" data-mp="1d5u1d3ovxcv8">
                            <span className="sr-only" data-id="element-1127" data-mp="19634ci8rm0i9">Skift status</span>
                            <select value={job.status} onChange={e => onSetStatus(job, e.target.value as JobStatus)} className="w-full rounded-md border border-stone-200 bg-stone-50 px-2 py-1.5 text-xs font-medium text-stone-700 outline-none focus:ring-2 focus:ring-brand-500" data-id="element-1128" data-mp="2rlmy4p107mg5">
                              {ORDER.map(option => <option key={option} value={option} data-id="element-1129" data-mp="23x3q8alv9bol">
                                  {JOB_STATUS[option].label}
                                </option>)}
                            </select>
                          </label>
                        </div>
                      </li>)}
                    {items.length === 0 && <li className="rounded-lg border border-dashed border-stone-200 px-3 py-6 text-center text-xs text-stone-400" data-id="element-1130" data-mp="1l6mar7q1ocl6">
                        Tom
                      </li>}
                  </ul>
                </section>;
        })}
          </div>
        </div>}
    </div>;
}